import React, { useEffect, useState, useCallback } from "react";
import {
  authMe, logout, loginUrl,
  adminDashboard, adminOrders, adminOrder, adminOrderAction,
  adminStock, adminAddMovement, adminProduct, adminUpdateProduct, adminVariants, adminAddVariant, adminUpdateVariant, adminTransfer,
  adminUsers, adminAddUser, adminUpdateUser, adminDeleteUser, adminResetDb,
  adminOverrideStatus, adminSetOther, adminSetNote, adminEditOrder,
  adminSubmitInventory, adminCorrections, adminApproveCorrection, adminRejectCorrection, adminCash, adminSaveCash,
  adminStockTakes, adminStockTakeAction, adminCashCountAction, adminMoneyCorrectionAction,
  adminLogs, adminAckLogs,
} from "../api";
import { kc } from "../pricing";

// stav = jen vyřízení (platba je nezávislá osa). PAID ponechán pro staré záznamy/labely.
const STATUS_LABELS = {
  NEW: "Nová", PACKED: "Zabalená", HANDED_OVER: "Předaná",
  SHIPPED: "Odeslaná", CANCELLED: "Stornovaná",
  PAID: "Zaplacená",
};
// stavy vyřízení nabízené v UI (bez zrušeného PAID)
const FULFILLMENT_STATES = ["NEW", "PACKED", "HANDED_OVER", "SHIPPED", "CANCELLED"];

const ROLE_LABELS = { VIEWER: "Pozorovatel", ADMIN: "Admin", SUPERADMIN: "Super admin" };

// role hierarchie pro UI gating (BE autorizaci stejně vynucuje sám)
const canWriteRole = (role) => role === "ADMIN" || role === "SUPERADMIN";
const isSuperadmin = (role) => role === "SUPERADMIN";

export default function Admin() {
  const [me, setMe] = useState(undefined); // undefined=loading, null=nepřihlášen
  // deep-link z e-mailu: /admin?order=<id> → rovnou otevřít objednávku
  const initialOrder = new URLSearchParams(window.location.search).get("order");
  const [tab, setTab] = useState(initialOrder ? "orders" : "dashboard");
  const [orderFilter, setOrderFilter] = useState(""); // sdílený filtr stavu objednávek

  const goToOrders = (status = "") => { setOrderFilter(status); setTab("orders"); };

  useEffect(() => {
    authMe().then(setMe).catch(() => setMe(null));
  }, []);

  if (me === undefined) return <div className="card">Ověřuji přihlášení…</div>;

  if (me === null) {
    return (
      <div className="card login">
        <h1>Administrace</h1>
        <a className="btn-google" href={loginUrl()}>Přihlásit přes Google</a>
      </div>
    );
  }

  const canWrite = canWriteRole(me.role);
  const superadmin = isSuperadmin(me.role);
  const alertCount = me.log_alert_count || 0;

  return (
    <div className="admin">
      <div className="admin-head">
        <div className="tabs">
          <button className={tab === "dashboard" ? "on" : ""} onClick={() => setTab("dashboard")}>Přehled</button>
          <button className={tab === "orders" ? "on" : ""} onClick={() => setTab("orders")}>Objednávky</button>
          <button className={tab === "stock" ? "on" : ""} onClick={() => setTab("stock")}>Sklad</button>
          {canWrite && (
            <button className={tab === "inventory" ? "on" : ""} onClick={() => setTab("inventory")}>Inventura</button>
          )}
          {superadmin && (
            <button className={tab === "corrections" ? "on" : ""} onClick={() => setTab("corrections")}>Korekce</button>
          )}
          {superadmin && (
            <button className={tab === "users" ? "on" : ""} onClick={() => setTab("users")}>Uživatelé</button>
          )}
          {superadmin && (
            <button className={tab === "superadmin" ? "on" : ""} onClick={() => setTab("superadmin")}>Superadmin</button>
          )}
          {superadmin && (
            <button className={tab === "log" ? "on" : ""} onClick={() => setTab("log")}>
              Log{alertCount > 0 && <span className="tab-alert">{alertCount}</span>}
            </button>
          )}
        </div>
        <div className="me">
          {me.email} · <span className="badge">{ROLE_LABELS[me.role] || me.role}</span>
          {" · "}<button className="link" onClick={() => logout().then(() => setMe(null))}>Odhlásit</button>
        </div>
      </div>
      {alertCount > 0 && tab !== "log" && (
        <div className="log-banner">
          <span>⚠️ V aplikačním logu {alertCount === 1 ? "je 1 nová chyba" : alertCount < 5 ? `jsou ${alertCount} nové chyby` : `je ${alertCount} nových chyb`}.</span>
          {superadmin && <button onClick={() => setTab("log")}>Zobrazit log</button>}
        </div>
      )}
      {tab === "dashboard" && <Dashboard onOrders={goToOrders} />}
      {tab === "orders" && (
        <Orders canWrite={canWrite} superadmin={superadmin}
                filter={orderFilter} setFilter={setOrderFilter} openOrderId={initialOrder} />
      )}
      {tab === "stock" && <Stock canWrite={canWrite} />}
      {tab === "inventory" && canWrite && <Inventory superadmin={superadmin} />}
      {tab === "corrections" && superadmin && <Corrections />}
      {tab === "users" && superadmin && <Users myEmail={me.email} />}
      {tab === "superadmin" && superadmin && <SuperadminPanel />}
      {tab === "log" && superadmin && <AppLog onAck={() => setMe({ ...me, log_alert_count: 0 })} />}
    </div>
  );
}

function useAsync(fn, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setError(null);
    fn().then(setData).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { data, error, reload };
}

function Section({ title, value, open, onToggle, children }) {
  return (
    <div className={`card accordion ${open ? "open" : ""}`}>
      <button type="button" className="accordion-head" onClick={onToggle}>
        <span className="accordion-title">{title}</span>
        <span className="accordion-value">{value} <span className="chev">{open ? "▾" : "▸"}</span></span>
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

function Dashboard({ onOrders }) {
  const { data, error } = useAsync(adminDashboard, []);
  const [open, setOpen] = useState({ revenue: false, profit: false, stock: false, graf: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;

  const other = data.revenue_other || 0;
  const costGoods = data.cost_goods || 0;
  const marginGoods = data.margin || 0;
  const dadShare = data.dad_share || 0;
  // témata 9b/10: řádky kategorie × sloupce Náklady | Zisk | Tržba
  const costTotal = costGoods + (data.revenue_shipping || 0);
  const profitCols = marginGoods + other;
  const profitTotal = data.profit != null ? data.profit : marginGoods + other;

  return (
    <>
      <Section title="Tržby" value={`${kc(data.revenue_total)} Kč`} open={open.revenue} onToggle={() => toggle("revenue")}>
        <div className="table-scroll">
          <table className="revenue cols">
            <thead><tr><th></th><th className="num">Náklady</th><th className="num">Zisk</th><th className="num">Tržba</th></tr></thead>
            <tbody>
              <tr>
                <td>Zboží</td>
                <td className="num">{kc(costGoods)} Kč</td>
                <td className="num">{kc(marginGoods)} Kč</td>
                <td className="num">{kc(data.revenue_products)} Kč</td>
              </tr>
              <tr>
                <td>Doprava</td>
                <td className="num">{kc(data.revenue_shipping)} Kč</td>
                <td className="num">0 Kč</td>
                <td className="num">{kc(data.revenue_shipping)} Kč</td>
              </tr>
              <tr>
                <td>Ostatní (dýško)</td>
                <td className="num">0 Kč</td>
                <td className="num">{kc(other)} Kč</td>
                <td className="num">{kc(other)} Kč</td>
              </tr>
              <tr className="sum">
                <td><strong>Součet</strong></td>
                <td className="num"><strong>{kc(costTotal)} Kč</strong></td>
                <td className="num"><strong>{kc(profitCols)} Kč</strong></td>
                <td className="num"><strong>{kc(data.revenue_total)} Kč</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* téma 5: kde peníze fyzicky jsou (jako v inventuře) + kontrolní součet proti tržbám */}
        <table className="revenue" style={{ marginTop: 10 }}>
          <tbody>
            <tr><td>Na účtu (bankovní převody)</td><td className="num">{kc(data.account_total)} Kč</td></tr>
            <tr><td>V pokladně (hotovost)</td><td className="num">{kc(data.cash_total)} Kč</td></tr>
            <tr className="sum"><td><strong>Součet</strong></td><td className="num"><strong>{kc((data.account_total ?? 0) + (data.cash_total ?? 0))} Kč</strong></td></tr>
            {((data.account_total ?? 0) + (data.cash_total ?? 0)) !== data.revenue_total && (
              <tr><td className="error">Rozdíl proti tržbám celkem</td>
                  <td className="num error"><strong>{kc(((data.account_total ?? 0) + (data.cash_total ?? 0)) - data.revenue_total)} Kč</strong></td></tr>
            )}
          </tbody>
        </table>
        <p className="muted small"><button className="link" onClick={() => onOrders()}>→ Otevřít objednávky</button></p>
      </Section>

      <Section title="Zisk" value={`${kc(profitTotal)} Kč`} open={open.profit} onToggle={() => toggle("profit")}>
        <table className="revenue">
          <tbody>
            <tr><td>Marže zboží</td><td className="num">{kc(marginGoods)} Kč</td></tr>
            {/* téma 11: marže zboží = marže táta + marže prodej */}
            <tr className="subrow"><td>└ marže táta ({data.dad_margin} Kč/pár)</td><td className="num">{kc(dadShare)} Kč</td></tr>
            <tr className="subrow"><td>└ marže prodej</td><td className="num">{kc(marginGoods - dadShare)} Kč</td></tr>
            {other > 0 && <tr><td>Ostatní (dýško)</td><td className="num">{kc(other)} Kč</td></tr>}
            <tr className="sum"><td><strong>Celkem</strong></td><td className="num"><strong>{kc(profitTotal)} Kč</strong></td></tr>
          </tbody>
        </table>
        {data.profit_by_person && data.profit_by_person.length > 0 && (
          <div className="subsection">
            <h4>Kdo kolik prodal a zařídil zisku (bez táta marže)</h4>
            <p className="muted small">Počítá se tomu, kdo objednávku <strong>zabalil</strong>; když se nebalilo, tomu, kdo ji předal/odeslal. (Hotovost se naopak eviduje u toho, kdo peníze vybral — viz Inventura.)</p>
            <div className="table-scroll">
              <table className="list">
                <thead><tr><th>Osoba</th><th className="num">Prodáno párů</th><th className="num">Zisk</th></tr></thead>
                <tbody>
                  {data.profit_by_person.map((p) => (
                    <tr key={p.who}>
                      <td>{p.who === "—" ? "nepřiřazeno" : p.who}</td>
                      <td className="num">{kc(p.pairs)}</td><td className="num">{kc(p.profit)} Kč</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      <Section title="Sklad" value={`${kc(data.stock_value)} Kč · ${kc(data.stock_pairs)} párů`} open={open.stock} onToggle={() => toggle("stock")}>
        <table className="revenue">
          <tbody>
            <tr><td>Nakoupeno</td><td className="num">{kc(data.total_pairs)} párů</td></tr>
            <tr><td>Na skladě — U táty</td><td className="num">{kc(data.stock_tata)}</td></tr>
            <tr><td>Na skladě — U holek</td><td className="num">{kc(data.stock_holky)}</td></tr>
            <tr><td>Zabaleno (mimo sklad, čeká na předání)</td><td className="num">{kc(data.packed_pairs || 0)}</td></tr>
            <tr><td>Prodáno (vyřízené)</td><td className="num">{kc(data.pairs_sold)}</td></tr>
            <tr className="sum"><td><strong>Zbývá na skladě</strong></td><td className="num"><strong>{kc(data.stock_pairs)} párů</strong></td></tr>
            <tr className="sub"><td>z toho zbývá prodat (volné, nerezervované)</td><td className="num">{kc(data.available_pairs ?? data.stock_pairs)} párů</td></tr>
          </tbody>
        </table>
        {(data.ordered_pairs ?? 0) > 0 && (
          <p className="muted small">
            Rozdíl {kc(data.ordered_pairs)} párů leží v regálu, ale je už rezervovaný
            v nezabalených objednávkách (nová / zaplacená) — znovu prodat nejde.
          </p>
        )}
      </Section>

      <Section title="Grafický přehled" value="doprodej v čase" open={open.graf} onToggle={() => toggle("graf")}>
        <DoprodejChart data={data} />
        {/* téma 4: tabulka aktuálních hodnot pod grafem (bez zmínky o párech) */}
        <table className="revenue" style={{ marginTop: 10 }}>
          <thead><tr><th></th><th className="num">Aktuálně</th><th className="num">Cíl</th><th className="num">Plnění</th></tr></thead>
          <tbody>
            <tr>
              <td>Sklad</td>
              <td className="num">{kc(data.stock_value)} Kč</td>
              <td className="num">0 Kč</td>
              <td className="num">{data.total_pairs ? Math.round(100 * (1 - data.stock_pairs / data.total_pairs)) : 0} %</td>
            </tr>
            <tr>
              <td>Tržby</td>
              <td className="num">{kc(data.revenue_total)} Kč</td>
              <td className="num">{kc(data.threshold_trzby)} Kč</td>
              <td className="num">{data.threshold_trzby ? Math.round(100 * data.revenue_total / data.threshold_trzby) : 0} %</td>
            </tr>
            <tr>
              <td>Zisk</td>
              <td className="num">{kc(data.profit)} Kč</td>
              <td className="num">{kc(data.threshold_zisk)} Kč</td>
              <td className="num">{data.threshold_zisk ? Math.round(100 * data.profit / data.threshold_zisk) : 0} %</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <div className="card">
        <h3>Objednávky dle stavu</h3>
        {data.packed_count > 0 && (
          <p className="packed-banner">
            📦 Nachystáno k předání: <strong>{kc(data.packed_count)}</strong>{" "}
            {data.packed_count === 1 ? "objednávka" : "objednávek"}{" "}
            ({kc(data.packed_personal)} osobně / {kc(data.packed_shipping)} Zásilkovna){" "}
            <button className="link" onClick={() => onOrders("PACKED")}>zobrazit</button>
          </p>
        )}
        <ul className="status-list">
          {FULFILLMENT_STATES.map((s) => (
            <li key={s}>
              <button className="link" onClick={() => onOrders(s)}>{STATUS_LABELS[s]}</button>
              : <strong>{kc(data.orders_by_status[s])}</strong>
            </li>
          ))}
          <li>
            <button className="link" onClick={() => onOrders("UNPAID")}>Nezaplacené</button>
            : <strong>{kc(data.unpaid_count)}</strong>
          </li>
          <li>
            <button className="link" onClick={() => onOrders()}>Celkem objednávek</button>
            : <strong>{kc(data.total_orders ?? Object.values(data.orders_by_status).reduce((a, b) => a + b, 0))}</strong>
          </li>
        </ul>
      </div>
    </>
  );
}

// Grafický přehled doprodeje (D-020) — čisté SVG bez závislostí.
// Plocha sklad↓, plocha zisk↑, čára tržby. Vodorovné čárkované prahy = očekávaný
// zisk a očekávané tržby. Granularita času je adaptivní (BE), bez predikce.
function DoprodejChart({ data }) {
  const points = data.timeseries || [];
  const thrZisk = data.threshold_zisk || 0;
  const thrTrzby = data.threshold_trzby || 0;
  if (points.length < 2) {
    return <p className="muted small">Zatím není co kreslit — přibyde s prvními zaplacenými objednávkami.</p>;
  }
  const W = 520, H = 240, P = { l: 52, r: 16, t: 14, b: 26 };
  const iw = W - P.l - P.r, ih = H - P.t - P.b;
  const n = points.length;
  const maxV = Math.max(
    thrZisk, thrTrzby,
    ...points.map((p) => Math.max(p.sklad || 0, p.zisk || 0, p.trzby || 0)),
    1
  );
  const x = (i) => P.l + (n === 1 ? 0 : (i / (n - 1)) * iw);
  const y = (v) => P.t + ih - (v / maxV) * ih;
  const linePath = (key) => points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p[key] || 0).toFixed(1)}`).join(" ");
  const areaPath = (key) =>
    `M${x(0).toFixed(1)},${(P.t + ih).toFixed(1)} ` +
    points.map((p, i) => `L${x(i).toFixed(1)},${y(p[key] || 0).toFixed(1)}`).join(" ") +
    ` L${x(n - 1).toFixed(1)},${(P.t + ih).toFixed(1)} Z`;
  const yz = y(thrZisk), yt = y(thrTrzby);
  const yticks = [0, maxV / 2, maxV];
  const step = Math.max(1, Math.ceil(n / 8));  // ať se popisky osy X nepřekrývají
  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="doprodej-chart" preserveAspectRatio="xMidYMid meet">
        {yticks.map((v, i) => (
          <g key={i}>
            <line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} className="grid" />
            <text x={P.l - 6} y={y(v) + 3} className="ytick">{kc(Math.round(v))}</text>
          </g>
        ))}
        <path d={areaPath("sklad")} className="area-stock" />
        <path d={areaPath("zisk")} className="area-profit" />
        <path d={linePath("trzby")} className="line-rev" />
        {thrTrzby > 0 && <line x1={P.l} x2={W - P.r} y1={yt} y2={yt} className="thr thr-trzby" />}
        {thrZisk > 0 && <line x1={P.l} x2={W - P.r} y1={yz} y2={yz} className="thr thr-zisk" />}
        {thrTrzby > 0 && <text x={W - P.r} y={yt - 3} className="thr-label thr-trzby-l" textAnchor="end">{kc(Math.round(thrTrzby))}</text>}
        {thrZisk > 0 && <text x={W - P.r} y={yz - 3} className="thr-label thr-zisk-l" textAnchor="end">{kc(Math.round(thrZisk))}</text>}
        {/* téma 12: aktuální hodnoty na konci čar (stejný styl jako prahy) */}
        {(() => {
          const last = points[n - 1];
          const series = [
            ["sklad", "thr-label end-sklad"],
            ["zisk", "thr-label thr-zisk-l"],
            ["trzby", "thr-label thr-trzby-l"],
          ];
          // rozhoď svisle, ať se popisky nepřekrývají (min. 10 px od sebe)
          const items = series
            .map(([key, cls]) => ({ key, cls, v: last[key] || 0, y: y(last[key] || 0) }))
            .sort((a, b) => a.y - b.y);
          for (let i = 1; i < items.length; i += 1) {
            if (items[i].y - items[i - 1].y < 10) items[i].y = items[i - 1].y + 10;
          }
          return items.map((it) => (
            <text key={it.key} x={x(n - 1) - 3} y={it.y - 4} className={it.cls} textAnchor="end">
              {kc(Math.round(it.v))}
            </text>
          ));
        })()}
        {points.map((p, i) => ((i % step === 0 || i === n - 1)
          ? <text key={i} x={x(i)} y={H - 8} className="xtick" textAnchor="middle">{p.t}</text> : null))}
      </svg>
      <div className="chart-legend">
        <span><i className="sw sw-stock" /> sklad</span>
        <span><i className="sw sw-profit" /> zisk</span>
        <span><i className="sw sw-rev" /> tržby</span>
        <span><i className="sw sw-thr-zisk" /> cíl zisk</span>
        <span><i className="sw sw-thr-trzby" /> cíl tržby</span>
      </div>
      <p className="muted small">Granularita: {data.graph_granularity || "po dnech"} · prahy = očekávaný zisk / tržby při doprodeji za aktuální cenu.</p>
    </div>
  );
}

function Orders({ canWrite, superadmin, filter, setFilter, openOrderId }) {
  const { data, error, reload } = useAsync(() => adminOrders(filter), [filter]);
  const dash = useAsync(adminDashboard, []);   // souhrn dle stavů nad tabulkou (téma 7)
  const [detail, setDetail] = useState(null);
  const [actErr, setActErr] = useState(null);

  // deep-link z e-mailu: otevři konkrétní objednávku po načtení
  useEffect(() => {
    if (openOrderId) adminOrder(openOrderId).then(setDetail).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function act(id, action) {
    setActErr(null);
    try {
      await adminOrderAction(id, action);
      reload();
      if (detail && detail.id === id) setDetail(await adminOrder(id));
    } catch (e) {
      setActErr(e.message || "Akce se nezdařila.");
    }
  }
  async function override(id, status) {
    await adminOverrideStatus(id, status);
    reload();
    if (detail && detail.id === id) setDetail(await adminOrder(id));
  }
  async function setOther(id, amount) {
    await adminSetOther(id, amount);
    reload();
    if (detail && detail.id === id) setDetail(await adminOrder(id));
  }
  async function setNote(id, note) {
    await adminSetNote(id, note);
    if (detail && detail.id === id) setDetail(await adminOrder(id));
  }
  async function editOrder(id, payload) {
    setActErr(null);
    try {
      await adminEditOrder(id, payload);
      reload();
      if (detail && detail.id === id) setDetail(await adminOrder(id));
    } catch (e) {
      setActErr(e.message || "Úprava se nezdařila.");
      throw e;
    }
  }

  if (error) return <div className="card error">{error}</div>;
  const d = dash.data;
  return (
    <div className="card">
      {d && (
        <ul className="status-list orders-summary">
          {FULFILLMENT_STATES.map((s) => (
            <li key={s}>
              <button className="link" onClick={() => setFilter(s)}>{STATUS_LABELS[s]}</button>
              : <strong>{kc(d.orders_by_status[s])}</strong>
            </li>
          ))}
          <li>
            <button className="link" onClick={() => setFilter("UNPAID")}>Nezaplacené</button>
            : <strong>{kc(d.unpaid_count)}</strong>
          </li>
          {d.packed_count > 0 && (
            <li className="packed-hint">
              📦 <button className="link" onClick={() => setFilter("PACKED")}>Nachystané</button>
              : <strong>{kc(d.packed_count)}</strong>{" "}
              <span className="muted small">
                ({kc(d.packed_personal)} k předání / {kc(d.packed_shipping)} k odvozu)
              </span>
            </li>
          )}
          <li>
            <button className="link" onClick={() => setFilter("")}>Celkem</button>
            : <strong>{kc(d.total_orders)}</strong>
          </li>
        </ul>
      )}
      <div className="row-between">
        <h3>Objednávky</h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">Všechny stavy</option>
          <option value="SOLD">Vyřízené (prodané)</option>
          <option value="UNPAID">Nezaplacené</option>
          <option value="PAID">Zaplacené</option>
          {FULFILLMENT_STATES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>
      {!data ? <p>Načítám…</p> : (
        <div className="table-scroll">
          <table className="list">
            <thead><tr><th>#</th><th>Zákazník</th><th className="num">Celkem</th><th>Stav</th><th>Platba</th><th>Vyřizuje</th><th></th></tr></thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id} className={o.status === "PACKED" ? "row-packed" : ""}>
                  <td>{o.status === "PACKED" && <span title="Zabalená — nachystaná">📦 </span>}{o.id}</td>
                  <td>{o.customer_name}</td><td className="num">{kc(o.total)} Kč</td>
                  <td><span className={`badge ${o.status}`}>{STATUS_LABELS[o.status]}</span></td>
                  <td><span className={`badge ${o.paid ? "paid-yes" : "paid-no"}`}>{o.paid ? "Zaplaceno" : "Nezaplaceno"}</span></td>
                  <td>{o.handled_by || "—"}</td>
                  <td><button className="link" onClick={async () => setDetail(await adminOrder(o.id))}>Detail</button></td>
                </tr>
              ))}
              {!data.orders.length && <tr><td colSpan="7" className="muted">Žádné objednávky.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {detail && <OrderDetail order={detail} onClose={() => { setDetail(null); setActErr(null); }} onAct={act}
                              canWrite={canWrite} superadmin={superadmin} actErr={actErr}
                              onOverride={override} onSetOther={setOther} onSetNote={setNote} onEdit={editOrder} />}
    </div>
  );
}

function OrderDetail({ order, onClose, onAct, canWrite, superadmin, actErr, onOverride, onSetOther, onSetNote, onEdit }) {
  const o = order;
  // cena objednávky bez přeplatku (zboží + doprava) — od ní se počítá přeplatek
  const baseAmount = (o.total ?? 0) - (o.other_amount ?? 0);
  const [received, setReceived] = useState(String(o.total ?? 0));
  const overpay = (Number(received) || 0) - baseAmount;
  const [note, setNote] = useState(o.notes ?? "");
  const [editOpen, setEditOpen] = useState(false);
  // Akce se skládají ze dvou nezávislých os: platba + vyřízení.
  const actions = [];
  if (canWrite && o.status !== "CANCELLED") {
    actions.push(o.paid ? ["unpay", "Zrušit platbu"] : ["pay", "Označit zaplaceno"]);
    if (o.status === "NEW") actions.push(["pack", "Zabaleno"]);
    // vyřídit i stornovat jde z nové i zabalené
    if (o.status === "NEW" || o.status === "PACKED") {
      actions.push(["hand-over", "Předáno osobně"]);
      actions.push(["ship", "Odesláno"]);
      if (!o.paid) actions.push(["pay-and-hand-over", "Zaplaceno + předáno"]);
      actions.push(["cancel", "Stornovat"]);
    }
  }
  const isZasilkovna = o.delivery_method === "ZASILKOVNA";
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="row-between"><h3>Objednávka #{o.id}</h3><button className="link" onClick={onClose}>Zavřít</button></div>
        <p>{o.customer_name} · {o.customer_email} · {o.customer_phone || "—"}</p>
        <p>Doručení: {o.delivery_method}{o.pickup_point_name ? ` — ${o.pickup_point_name}` : ""}{o.pickup_point_id ? ` (${o.pickup_point_id})` : ""} · Platba: {o.payment_method}</p>
        <p>Stav: <span className={`badge ${o.status}`}>{STATUS_LABELS[o.status]}</span>
          {" "}<span className={`badge ${o.paid ? "paid-yes" : "paid-no"}`}>{o.paid ? "Zaplaceno" : "Nezaplaceno"}</span>
          {o.packed_at && (
            <span className="muted small"> · zabaleno {o.packed_at.slice(0, 16).replace("T", " ")}</span>
          )}</p>
        {(o.packed_by || o.handled_by) && (
          <p className="small">
            {o.packed_by && <>📦 Zabalil(a): <strong>{o.packed_by}</strong> <span className="muted">(jemu/jí se počítá zisk)</span></>}
            {o.packed_by && o.handled_by && " · "}
            {o.handled_by && <>Vyřídil(a): <strong>{o.handled_by}</strong>{o.payment_method === "CASH" && o.paid && <span className="muted"> (u něj/ní je hotovost)</span>}</>}
          </p>
        )}
        {o.customer_note && <p className="customer-note">💬 Poznámka zákazníka: {o.customer_note}</p>}
        <table className="list">
          <thead><tr><th>Velikost</th><th className="num">Ks</th><th className="num">Cena</th><th className="num">Celkem</th></tr></thead>
          <tbody>
            {o.items.map((it) => (
              <tr key={it.id}><td>{it.variant_label}</td><td className="num">{it.quantity}</td><td className="num">{kc(it.unit_price)} Kč</td><td className="num">{kc(it.line_total)} Kč</td></tr>
            ))}
          </tbody>
        </table>
        <p className="total-line">
          Celkem {kc(o.total)} Kč (z toho doprava {kc(o.shipping_cost)} Kč)
          {o.other_amount > 0 && <> · ostatní (dýško) {kc(o.other_amount)} Kč</>}
        </p>
        {canWrite && (
          <div className="other-input">
            <label className="small">Opravdu obdrženo (Kč) — kolik zákazník skutečně zaplatil
              <input type="number" min="0" step="1" value={received}
                     onChange={(e) => setReceived(e.target.value)} />
            </label>
            <p className={`small ${overpay < 0 ? "error" : "muted"}`}>
              Cena objednávky: {kc(baseAmount)} Kč →{" "}
              {overpay > 0 && <>přeplatek (dýško): <strong>{kc(overpay)} Kč</strong></>}
              {overpay === 0 && <>bez přeplatku</>}
              {overpay < 0 && <>nedoplatek: <strong>{kc(-overpay)} Kč</strong> — nelze uložit</>}
            </p>
            <button disabled={overpay < 0}
                    onClick={() => onSetOther(o.id, overpay)}>Uložit obdrženou částku</button>
          </div>
        )}
        {canWrite ? (
          <div className="other-input">
            <label className="small">Poznámka (interní — zákazník nevidí)
              <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <button onClick={() => onSetNote(o.id, note)}>Uložit poznámku</button>
          </div>
        ) : (o.notes && <p className="small muted">Poznámka: {o.notes}</p>)}
        <div className="actions">
          {actions.map(([a, label]) => (
            <button key={a} onClick={() => onAct(o.id, a)} className={a === "cancel" ? "danger" : ""}>{label}</button>
          ))}
        </div>
        {isZasilkovna && (
          <div className="packeta-box">
            {o.packet_dispatch_code ? (
              <div className="pickup-selected">
                📦 Podací kód: <strong>{o.packet_dispatch_code}</strong>
                {o.packet_id && <div className="small">Číslo zásilky: {o.packet_id}</div>}
              </div>
            ) : canWrite && o.status !== "CANCELLED" ? (
              <>
                {o.packet_id && <div className="small muted">Zásilka vytvořena (č. {o.packet_id}), chybí podací kód.</div>}
                <button onClick={() => onAct(o.id, "packeta-submit")}>
                  {o.packet_id ? "Dokončit podání (podací kód)" : "Podat zásilku (podací kód)"}
                </button>
              </>
            ) : null}
          </div>
        )}
        {actErr && <div className="error small">{actErr}</div>}
        {superadmin && (
          <div className="override">
            <label className="small muted">Opravit stav vyřízení (superadmin)
              <select value={FULFILLMENT_STATES.includes(o.status) ? o.status : "NEW"}
                      onChange={(e) => onOverride(o.id, e.target.value)}>
                {FULFILLMENT_STATES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </label>
            <p className="small muted">Přepíše stav mimo běžný postup; sklad neupravuje (řeš ručním pohybem).</p>
            <button className="link" onClick={() => setEditOpen((v) => !v)}>
              {editOpen ? "▾ Skrýt úpravu údajů" : "▸ Upravit údaje objednávky (superadmin)"}
            </button>
            {editOpen && <OrderEditForm order={o} onEdit={onEdit} />}
          </div>
        )}
        {o.history && o.history.length > 0 && (
          <div className="order-history">
            <h4>Historie objednávky</h4>
            <ul className="history-list">
              {o.history.map((h, i) => (
                <li key={i}>
                  <span className="muted small">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {" — "}{h.detail || h.action}
                  <span className="muted small"> ({h.actor || "—"})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Superadmin editace údajů objednávky (kontakt/doručení/platba/poznámky).
const DELIVERY_OPTS = [["PERSONAL", "Osobně"], ["ZASILKOVNA", "Zásilkovna"], ["ALZABOX", "AlzaBox"]];
const PAYMENT_OPTS = [["BANK_TRANSFER", "Převodem"], ["CASH", "Hotově"]];
function OrderEditForm({ order, onEdit }) {
  const [f, setF] = useState({
    customer_name: order.customer_name || "", customer_email: order.customer_email || "",
    customer_phone: order.customer_phone || "", delivery_method: order.delivery_method,
    payment_method: order.payment_method, pickup_point_id: order.pickup_point_id || "",
    pickup_point_name: order.pickup_point_name || "", delivery_addr: order.delivery_addr || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setSaved(false); };
  async function save() {
    setSaving(true);
    try { await onEdit(order.id, f); setSaved(true); } catch { /* chybu ukáže actErr */ } finally { setSaving(false); }
  }
  return (
    <div className="order-edit">
      <div className="grid2">
        <label className="small">Jméno<input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></label>
        <label className="small">E-mail<input type="email" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></label>
      </div>
      <div className="grid2">
        <label className="small">Telefon<input value={f.customer_phone} placeholder="+420123456789" onChange={(e) => set("customer_phone", e.target.value)} /></label>
        <label className="small">Doručení
          <select value={f.delivery_method} onChange={(e) => set("delivery_method", e.target.value)}>
            {DELIVERY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <div className="grid2">
        <label className="small">Platba
          <select value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
            {PAYMENT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="small">Výdejní místo (název)<input value={f.pickup_point_name} onChange={(e) => set("pickup_point_name", e.target.value)} /></label>
      </div>
      <div className="grid2">
        <label className="small">Výdejní místo (ID)<input value={f.pickup_point_id} onChange={(e) => set("pickup_point_id", e.target.value)} /></label>
        <label className="small">Adresa doručení<input value={f.delivery_addr} onChange={(e) => set("delivery_addr", e.target.value)} /></label>
      </div>
      <button onClick={save} disabled={saving}>{saving ? "Ukládám…" : "Uložit údaje"}</button>
      {saved && <span className="small" style={{ color: "#226622", marginLeft: 8 }}>✓ uloženo</span>}
    </div>
  );
}

const WAREHOUSES = [["TATA", "U táty"], ["HOLKY", "U holek"]];

// typy skladového pohybu pro jednotný formulář (znaménko se odvodí)
const MOVE_TYPES = [
  ["REPLENISH", "Doskladnění (od dodavatele) +", 1],
  ["RETURN", "Vratka na sklad +", 1],
  ["WRITE_OFF", "Odpis −", -1],
  ["GIFT", "Dar −", -1],
  ["TRANSFER", "Přeskladnění (mezi sklady)", 0],
];

function Stock({ canWrite }) {
  const { data, error, reload } = useAsync(adminStock, []);
  const [mv, setMv] = useState({ type: "REPLENISH", variant_id: "", quantity: "", location: "TATA", from: "TATA", to: "HOLKY", reason: "" });
  const [msg, setMsg] = useState(null);

  async function submitMove(e) {
    e.preventDefault();
    setMsg(null);
    const qty = Number(mv.quantity);
    if (!mv.variant_id || !(qty > 0)) { setMsg("Vyber velikost a zadej kladný počet."); return; }
    try {
      if (mv.type === "TRANSFER") {
        if (mv.from === mv.to) { setMsg("Zdrojový a cílový sklad musí být různé."); return; }
        await adminTransfer({ variant_id: Number(mv.variant_id), quantity: qty, from: mv.from, to: mv.to });
      } else {
        const sign = (MOVE_TYPES.find((t) => t[0] === mv.type) || [])[2] || 1;
        await adminAddMovement({
          variant_id: Number(mv.variant_id), type: mv.type,
          quantity_delta: sign * qty, location: mv.location, reason: mv.reason,
        });
      }
      setMv({ ...mv, quantity: "", reason: "" });
      reload();
    } catch (e2) { setMsg(e2.message); }
  }
  async function toggleActive(v) {
    await adminUpdateVariant(v.id, { active: !v.active });
    reload();
  }

  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;
  return (
    <>
      <div className="card">
        <h3>Sklad podle velikostí</h3>
        <div className="table-scroll">
          <table className="list">
            <thead><tr>
              <th>Velikost</th><th className="num">Nakoupeno</th><th className="num">U táty</th><th className="num">U holek</th><th className="num">Objednáno</th><th className="num">Zabaleno</th><th className="num">Prodáno</th><th>Stav</th>{canWrite && <th></th>}
            </tr></thead>
            <tbody>
              {data.variants.map((v) => (
                <tr key={v.id} className={v.active ? "" : "muted"}>
                  <td>{v.label}</td>
                  <td className="num"><strong>{kc((v.stock || 0) + (v.ordered || 0) + (v.packed || 0) + (v.sold || 0))}</strong></td>
                  <td className="num">{kc(v.stock_tata)}</td>
                  <td className="num">{kc(v.stock_holky)}</td>
                  <td className="num">{v.ordered ? `(${kc(v.ordered)})` : "—"}</td>
                  <td className="num packed-cell">{v.packed ? <strong>{kc(v.packed)}</strong> : "—"}</td>
                  <td className="num">{v.sold ?? 0}</td>
                  <td>{v.active ? "aktivní" : "skrytá"}</td>
                  {canWrite && <td><button className="link" onClick={() => toggleActive(v)}>{v.active ? "Skrýt" : "Obnovit"}</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">Objednáno = kusy v nezabalených objednávkách (nová/zaplacená) · Zabaleno = nachystané, už mimo sklad · Prodáno = ve vyřízených (předáno/odesláno).</p>
      </div>

      {canWrite && (
        <form className="card" onSubmit={submitMove}>
          <h3>Nový skladový pohyb</h3>
          <label>Typ pohybu
            <select value={mv.type} onChange={(e) => setMv({ ...mv, type: e.target.value })}>
              {MOVE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>Velikost
            <select value={mv.variant_id} onChange={(e) => setMv({ ...mv, variant_id: e.target.value })} required>
              <option value="">—</option>
              {data.variants.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </label>
          {mv.type === "TRANSFER" ? (
            <div className="grid2">
              <label>Z
                <select value={mv.from} onChange={(e) => setMv({ ...mv, from: e.target.value })}>
                  {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label>Do
                <select value={mv.to} onChange={(e) => setMv({ ...mv, to: e.target.value })}>
                  {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
          ) : (
            <label>Sklad
              <select value={mv.location} onChange={(e) => setMv({ ...mv, location: e.target.value })}>
                {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          )}
          <label>Počet párů
            <input type="number" min="1" value={mv.quantity}
                   onChange={(e) => setMv({ ...mv, quantity: e.target.value })} required />
          </label>
          {mv.type !== "TRANSFER" && (
            <label>Důvod (volitelné)
              <input value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} />
            </label>
          )}
          <button type="submit">Zapsat pohyb</button>
        </form>
      )}

      {msg && <div className="card error">{msg}</div>}

      <div className="card">
        <h3>Historie pohybů</h3>
        <div className="table-scroll">
          <table className="list">
            <thead><tr><th>Kdy</th><th>Velikost</th><th>Typ</th><th className="num">Δ</th><th>Sklad</th><th>Důvod</th><th>Obj.</th></tr></thead>
            <tbody>
              {data.movements.map((m) => (
                <tr key={m.id}>
                  <td>{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{m.variant_label}</td><td>{m.type}</td><td className="num">{m.quantity_delta}</td>
                  <td>{m.location === "TATA" ? "U táty" : m.location === "HOLKY" ? "U holek" : "—"}</td>
                  <td>{m.reason || "—"}</td><td>{m.order_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const ROLES = ["VIEWER", "ADMIN", "SUPERADMIN"];

function Users({ myEmail }) {
  const { data, error, reload } = useAsync(adminUsers, []);
  const [form, setForm] = useState({ email: "", role: "ADMIN", warehouse: "" });
  const [msg, setMsg] = useState(null);

  async function addUser(e) {
    e.preventDefault();
    setMsg(null);
    try {
      await adminAddUser({ email: form.email, role: form.role, warehouse: form.warehouse });
      setForm({ email: "", role: "ADMIN", warehouse: "" });
      reload();
    } catch (e2) { setMsg(e2.message); }
  }
  async function changeRole(u, role) {
    setMsg(null);
    try { await adminUpdateUser(u.id, { role }); reload(); } catch (e2) { setMsg(e2.message); }
  }
  async function changeWarehouse(u, warehouse) {
    setMsg(null);
    try { await adminUpdateUser(u.id, { warehouse }); reload(); } catch (e2) { setMsg(e2.message); }
  }
  async function setActive(u, active) {
    setMsg(null);
    try {
      if (active) await adminUpdateUser(u.id, { active: true });
      else await adminDeleteUser(u.id);
      reload();
    } catch (e2) { setMsg(e2.message); }
  }

  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;
  return (
    <>
      <div className="card">
        <h3>Uživatelé administrace</h3>
        <p className="muted small">
          Role: <strong>Pozorovatel</strong> jen čte · <strong>Admin</strong> spravuje objednávky a sklad ·
          <strong> Super admin</strong> navíc spravuje uživatele.
        </p>
        <div className="user-cards">
          {data.users.map((u) => {
            const self = u.email === myEmail;
            return (
              <div key={u.id} className={`user-card ${u.active ? "" : "off"}`}>
                <div className="user-card-head">
                  <strong>{u.email}{self && " (ty)"}</strong>
                  <span className="badge">{u.active ? "aktivní" : "deaktivovaný"}</span>
                </div>
                <div className="user-card-fields">
                  <label>Role
                    <select value={u.role} disabled={self}
                            onChange={(e) => changeRole(u, e.target.value)}>
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </label>
                  <label>Sklad
                    <select value={u.warehouse || ""}
                            onChange={(e) => changeWarehouse(u, e.target.value)}>
                      <option value="">—</option>
                      {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </label>
                </div>
                {!self && (
                  <div className="user-card-actions">
                    {u.active
                      ? <button className="link danger" onClick={() => setActive(u, false)}>Deaktivovat</button>
                      : <button className="link" onClick={() => setActive(u, true)}>Aktivovat</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <form className="card" onSubmit={addUser}>
        <h3>Přidat uživatele</h3>
        <label>E-mail (Google)
          <input type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        </label>
        <label>Role
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </label>
        <label>Sklad (odkud prodává)
          <select value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })}>
            <option value="">— žádný —</option>
            {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button type="submit">Přidat</button>
        <p className="muted small">Uživatel se musí přihlásit svým Google účtem; e-mail musí být i mezi „Test users" v Google OAuth consent screenu.</p>
      </form>

      {msg && <div className="card error">{msg}</div>}
    </>
  );
}

const LOG_LEVEL_LABELS = { INFO: "Info", WARNING: "Varování", ERROR: "Chyba" };
const LOG_CATEGORY_LABELS = {
  auth: "Přihlášení", users: "Uživatelé", danger_zone: "Nebezpečné akce", error: "Chyba aplikace",
};

function AppLog({ onAck }) {
  const { data, error, reload } = useAsync(adminLogs, []);
  const [acking, setAcking] = useState(false);
  const [msg, setMsg] = useState(null);

  async function ack() {
    setAcking(true); setMsg(null);
    try {
      await adminAckLogs();
      reload();
      onAck && onAck();
    } catch (e2) { setMsg(e2.message); }
    finally { setAcking(false); }
  }

  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;
  const unacked = data.logs.filter((l) => l.level === "ERROR" && !l.acknowledged).length;

  return (
    <div className="card">
      <div className="row-between">
        <h3>Aplikační log</h3>
        {unacked > 0 && (
          <button onClick={ack} disabled={acking}>
            {acking ? "Označuji…" : `Označit ${unacked} ${unacked === 1 ? "chybu" : unacked < 5 ? "chyby" : "chyb"} jako přečtené`}
          </button>
        )}
      </div>
      <p className="muted small">
        Audit akcí administrátorů (přihlášení, správa uživatelů, nebezpečné akce) a
        neošetřené chyby backendu — posledních {data.logs.length} záznamů.
      </p>
      {msg && <div className="error">{msg}</div>}
      <div className="table-scroll">
        <table className="list">
          <thead>
            <tr><th>Kdy</th><th>Úroveň</th><th>Kategorie</th><th>Kdo</th><th>Zpráva</th></tr>
          </thead>
          <tbody>
            {data.logs.map((l) => (
              <tr key={l.id} className={`log-row ${l.level}`}>
                <td>{new Date(l.created_at).toLocaleString("cs-CZ")}</td>
                <td><span className={`log-level ${l.level}`}>{LOG_LEVEL_LABELS[l.level] || l.level}</span></td>
                <td>{LOG_CATEGORY_LABELS[l.category] || l.category}</td>
                <td>{l.actor || "—"}</td>
                <td>{l.message}{l.detail && <div className="muted small">{l.detail}</div>}</td>
              </tr>
            ))}
            {data.logs.length === 0 && <tr><td colSpan="5" className="muted">Zatím žádné záznamy.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductEditor() {
  const { data, error, reload } = useAsync(adminProduct, []);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (data) setForm({
      name: data.name || "",
      announcement: data.announcement || "",
      unit_cost: String(data.unit_cost ?? 0),
      dad_margin: String(data.dad_margin ?? 3),
      tiers: (data.price_tiers || []).map((t) => ({
        min_quantity: String(t.min_quantity), unit_price: String(t.unit_price),
      })),
    });
  }, [data]);

  function setTier(i, k, v) { setForm((f) => ({ ...f, tiers: f.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) })); }
  function addTier() { setForm((f) => ({ ...f, tiers: [...f.tiers, { min_quantity: "", unit_price: "" }] })); }
  function removeTier(i) { setForm((f) => ({ ...f, tiers: f.tiers.filter((_, j) => j !== i) })); }

  async function save() {
    setMsg(null);
    try {
      await adminUpdateProduct({
        name: form.name,
        announcement: form.announcement,
        unit_cost: Number(form.unit_cost || 0),
        dad_margin: Number(form.dad_margin || 0),
        price_tiers: form.tiers
          .filter((t) => t.min_quantity !== "" && t.unit_price !== "")
          .map((t) => ({ min_quantity: Number(t.min_quantity), unit_price: Number(t.unit_price) })),
      });
      setMsg("Uloženo ✓");
      reload();
    } catch (e) { setMsg(e.message); }
  }

  if (error) return <div className="card error">{error}</div>;
  if (!form) return <div className="card">Načítám produkt…</div>;
  return (
    <div className="card">
      <h3>Produkt — obsah a ceny</h3>
      <label>Název
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label>Nákupní cena za pár (Kč) — pro výpočet marže
        <input type="number" min="0" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
      </label>
      <label>Táta marže (Kč/pár) — podíl táty na zisku z každého prodaného páru
        <input type="number" min="0" value={form.dad_margin} onChange={(e) => setForm({ ...form, dad_margin: e.target.value })} />
      </label>
      <label>Oznámení pro zákazníky — zobrazí se v potvrzení objednávky (prázdné = nezobrazuje se)
        <textarea className="note-field" rows="2" value={form.announcement}
                  onChange={(e) => setForm({ ...form, announcement: e.target.value })} />
      </label>
      <h4>Cenové stupně (množstevní sleva)</h4>
      <p className="muted small">Cena za pár podle celkového počtu kusů v objednávce. Nejnižší stupeň musí být „od 1".</p>
      <table className="list">
        <thead><tr><th>Od kusů</th><th>Cena / pár (Kč)</th><th></th></tr></thead>
        <tbody>
          {form.tiers.map((t, i) => (
            <tr key={i}>
              <td><input type="number" min="1" style={{ width: 80 }} value={t.min_quantity}
                         onChange={(e) => setTier(i, "min_quantity", e.target.value)} /></td>
              <td><input type="number" min="0" style={{ width: 90 }} value={t.unit_price}
                         onChange={(e) => setTier(i, "unit_price", e.target.value)} /></td>
              <td><button className="link danger" onClick={() => removeTier(i)}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="link" onClick={addTier}>+ přidat stupeň</button>
      <div style={{ marginTop: 8 }}><button onClick={save}>Uložit produkt a ceny</button></div>
      {msg && <div className={msg.startsWith("Uloženo") ? "small" : "error"}>{msg}</div>}
    </div>
  );
}

function SuperadminPanel() {
  const { data, error, reload } = useAsync(adminVariants, []);
  const [nv, setNv] = useState({ label: "", initial_quantity: "", location: "TATA" });
  const [msg, setMsg] = useState(null);
  const [resetMsg, setResetMsg] = useState(null);
  const [resetting, setResetting] = useState(false);

  async function addVariant(e) {
    e.preventDefault();
    setMsg(null);
    try {
      await adminAddVariant({
        label: nv.label,
        initial_quantity: Number(nv.initial_quantity || 0),
        location: nv.location,
      });
      setNv({ label: "", initial_quantity: "", location: nv.location });
      reload();
    } catch (e2) { setMsg(e2.message); }
  }

  async function resetDb() {
    if (!window.confirm(
      "Opravdu vynulovat databázi?\n\nSmaže VŠECHNY objednávky, skladové pohyby a produkt " +
      "a založí čistý stav (velikosti, sklad 0). Uživatelé zůstanou. Nevratné!"
    )) return;
    setResetMsg(null); setResetting(true);
    try {
      const r = await adminResetDb();
      setResetMsg(`Hotovo — databáze vynulována (smazáno objednávek: ${r.deleted.orders}, pohybů: ${r.deleted.stock_movements}).`);
      reload();
    } catch (e2) { setResetMsg(e2.message); }
    finally { setResetting(false); }
  }

  return (
    <>
      <ProductEditor />

      <form className="card" onSubmit={addVariant}>
        <h3>Přidat velikost</h3>
        <p className="muted small">Přidání nové velikosti do číselníku (jen superadmin).</p>
        <label>Název (label)
          <input value={nv.label} onChange={(e) => setNv({ ...nv, label: e.target.value })} required />
        </label>
        <label>Počáteční množství
          <input type="number" min="0" value={nv.initial_quantity}
                 onChange={(e) => setNv({ ...nv, initial_quantity: e.target.value })} />
        </label>
        <label>Sklad
          <select value={nv.location} onChange={(e) => setNv({ ...nv, location: e.target.value })}>
            {WAREHOUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button type="submit">Přidat</button>
        {msg && <div className="error">{msg}</div>}
        {error && <div className="error">{error}</div>}
        {data && data.variants && (
          <p className="muted small">Stávající velikosti: {data.variants.map((v) => v.label).join(", ") || "—"}</p>
        )}
      </form>

      <div className="card danger-zone">
        <h3>⚠️ Vynulovat databázi</h3>
        <p className="muted small">
          Smaže všechny objednávky, skladové pohyby a produkt a založí čistý stav
          (velikosti, sklad 0). Uživatelé administrace zůstanou. Nevratné.
        </p>
        <button className="danger" onClick={resetDb} disabled={resetting}>
          {resetting ? "Mažu…" : "Vynulovat databázi"}
        </button>
        {resetMsg && <p className="small">{resetMsg}</p>}
      </div>
    </>
  );
}

const CORR_STATUS = { PENDING: "čeká", APPROVED: "schváleno", REJECTED: "zamítnuto" };

function Inventory({ superadmin }) {
  const { data, error, reload } = useAsync(adminStock, []);
  const takes = useAsync(adminStockTakes, []);
  const cash = useAsync(adminCash, []);
  const reloadAll = () => { reload(); takes.reload(); cash.reload(); };
  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;
  return (
    <>
      <WarehouseInventory location="TATA" label="U táty" stockKey="stock_tata" packedKey="packed_tata" variants={data.variants} onDone={reloadAll} />
      <WarehouseInventory location="HOLKY" label="U holek" stockKey="stock_holky" packedKey="packed_holky" variants={data.variants} onDone={reloadAll} />
      <CashInventory cash={cash} />
      <InventoryHistory takes={takes} cash={cash} superadmin={superadmin} />
    </>
  );
}

// témata 1+2: VŠECHNY proběhlé inventury (sklady + účet + pokladna) v jedné tabulce,
// jednotný režim: detail + schvalování superadminem (u peněz vč. korekcí při rozdílu)
function InventoryHistory({ takes, cash, superadmin }) {
  const [detail, setDetail] = useState(null);       // skladová inventura
  const [moneyDetail, setMoneyDetail] = useState(null); // peněžní inventura
  const [msg, setMsg] = useState(null);
  const LOC_LABELS = { TATA: "Sklad — U táty", HOLKY: "Sklad — U holek" };
  const ST_LABELS = { PENDING: "Čeká na schválení", APPROVED: "Schválená", REJECTED: "Zamítnutá" };

  async function act(id, action) {
    setMsg(null);
    try {
      const updated = await adminStockTakeAction(id, action);
      setDetail(updated);
      takes.reload();
    } catch (e) { setMsg(e.message); }
  }
  async function actMoney(id, action) {
    setMsg(null);
    try {
      const updated = await adminCashCountAction(id, action);
      setMoneyDetail(updated);
      cash.reload();
    } catch (e) { setMsg(e.message); }
  }

  const stockRows = ((takes.data && takes.data.stock_takes) || []).map((t) => ({
    key: `st-${t.id}`, when: t.created_at, type: LOC_LABELS[t.location] || t.location,
    who: t.counted_by,
    result: t.diff_count ? `⚠️ ${t.diff_count} rozdílů` : "✓ bez rozdílu",
    status: <span className={`badge st-${t.status}`}>{ST_LABELS[t.status] || t.status}</span>,
    action: <button className="link" onClick={() => setDetail(t)}>Detail</button>,
  }));
  const moneyRows = ((cash.data && cash.data.history) || []).map((c) => ({
    key: `cc-${c.id}`, when: c.created_at,
    type: (c.kind || "CASH") === "ACCOUNT" ? "Účet" : "Pokladna",
    who: c.counted_by,
    result: c.delta ? `⚠️ rozdíl ${c.delta > 0 ? `+${kc(c.delta)}` : kc(c.delta)} Kč` : "✓ bez rozdílu",
    status: <span className={`badge st-${c.status || "PENDING"}`}>{ST_LABELS[c.status] || c.status}</span>,
    action: <button className="link" onClick={() => setMoneyDetail(c)}>Detail</button>,
  }));
  const list = [...stockRows, ...moneyRows].sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  return (
    <div className="card">
      <h3>Proběhlé inventury</h3>
      {!list.length && <p className="muted small">Zatím žádná inventura.</p>}
      {list.length > 0 && (
        <div className="table-scroll">
          <table className="list">
            <thead><tr><th>Kdy</th><th>Co</th><th>Kdo</th><th>Výsledek</th><th>Stav</th><th></th></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.key}>
                  <td>{(r.when || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{r.type}</td>
                  <td>{r.who || "—"}</td>
                  <td>{r.result}</td>
                  <td>{r.status || "—"}</td>
                  <td>{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <div className="row-between">
              <h3>Inventura #{detail.id} — {LOC_LABELS[detail.location] || detail.location}</h3>
              <button className="link" onClick={() => setDetail(null)}>Zavřít</button>
            </div>
            <p className="small">
              Provedl: <strong>{detail.counted_by || "—"}</strong> · {(detail.created_at || "").slice(0, 16).replace("T", " ")}<br />
              Stav: <strong>{ST_LABELS[detail.status]}</strong>
              {detail.reviewed_by && <> · {detail.status === "APPROVED" ? "schválil" : "zamítl"} <strong>{detail.reviewed_by}</strong> {(detail.reviewed_at || "").slice(0, 16).replace("T", " ")}</>}
            </p>
            <div className="table-scroll">
              <table className="list">
                <thead><tr><th>Velikost</th><th className="num">Evidence</th><th className="num">Napočítáno</th><th className="num">Rozdíl</th></tr></thead>
                <tbody>
                  {(detail.rows || []).map((r, i) => (
                    <tr key={i} className={r.delta ? "diff" : ""}>
                      <td>{r.variant_label}</td><td className="num">{r.expected}</td><td className="num">{r.physical}</td>
                      <td className="num">{r.delta > 0 ? `+${r.delta}` : r.delta || "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {detail.diff_count > 0 && (
              <p className="muted small">Rozdíly se řeší jako korekce v agendě Korekce (schvalují se tam jednotlivě).</p>
            )}
            {superadmin && detail.status === "PENDING" && (
              <div className="row-between" style={{ marginTop: 8 }}>
                <button onClick={() => act(detail.id, "approve")}>Schválit inventuru</button>
                <button className="danger" onClick={() => act(detail.id, "reject")}>Zamítnout</button>
              </div>
            )}
            {msg && <div className="error">{msg}</div>}
          </div>
        </div>
      )}
      {moneyDetail && (
        <div className="modal-backdrop" onClick={() => setMoneyDetail(null)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <div className="row-between">
              <h3>Inventura #{moneyDetail.id} — {(moneyDetail.kind || "CASH") === "ACCOUNT" ? "Účet" : "Pokladna"}</h3>
              <button className="link" onClick={() => setMoneyDetail(null)}>Zavřít</button>
            </div>
            <p className="small">
              Provedl: <strong>{moneyDetail.counted_by || "—"}</strong> · {(moneyDetail.created_at || "").slice(0, 16).replace("T", " ")}<br />
              Stav: <strong>{ST_LABELS[moneyDetail.status] || moneyDetail.status}</strong>
              {moneyDetail.reviewed_by && <> · {moneyDetail.status === "APPROVED" ? "schválil" : "zamítl"} <strong>{moneyDetail.reviewed_by}</strong> {(moneyDetail.reviewed_at || "").slice(0, 16).replace("T", " ")}</>}
            </p>
            <table className="revenue">
              <tbody>
                <tr><td>Očekáváno</td><td className="num">{kc(moneyDetail.expected)} Kč</td></tr>
                <tr><td>Napočítáno</td><td className="num">{kc(moneyDetail.physical)} Kč</td></tr>
                <tr className={moneyDetail.delta ? "diff" : ""}>
                  <td><strong>Rozdíl</strong></td>
                  <td className="num"><strong>{moneyDetail.delta > 0 ? `+${kc(moneyDetail.delta)}` : kc(moneyDetail.delta)} Kč</strong></td>
                </tr>
              </tbody>
            </table>
            {moneyDetail.delta !== 0 && (
              <p className="muted small">Rozdíl se řeší jako korekce peněz v agendě Korekce.</p>
            )}
            {superadmin && (moneyDetail.status || "PENDING") === "PENDING" && (
              <div className="row-between" style={{ marginTop: 8 }}>
                <button onClick={() => actMoney(moneyDetail.id, "approve")}>Schválit inventuru</button>
                <button className="danger" onClick={() => actMoney(moneyDetail.id, "reject")}>Zamítnout</button>
              </div>
            )}
            {msg && <div className="error">{msg}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function WarehouseInventory({ location, label, stockKey, packedKey, variants, onDone }) {
  const [counts, setCounts] = useState({});
  const [msg, setMsg] = useState(null);
  async function submit() {
    setMsg(null);
    const items = Object.entries(counts)
      .filter(([, v]) => v !== "" && v != null)
      .map(([vid, physical]) => ({ variant_id: Number(vid), physical: Number(physical) }));
    if (!items.length) { setMsg("Zadej aspoň jeden napočítaný počet."); return; }
    try {
      const r = await adminSubmitInventory({ location, counts: items });
      setMsg(r.created.length
        ? `Inventura zaznamenána, návrhů korekcí: ${r.created.length} — korekce čekají na schválení v agendě Korekce.`
        : "Inventura zaznamenána — vše sedí, bez korekcí. Čeká na schválení superadminem (seznam dole).");
      setCounts({}); onDone();
    } catch (e) { setMsg(e.message); }
  }
  return (
    <div className="card">
      <h3>Inventura skladu — {label}</h3>
      <div className="table-scroll">
        <table className="list">
          <thead><tr><th>Velikost</th><th className="num">Evidence</th><th className="num">Objednáno</th><th className="num">📦 Zabaleno</th><th>Napočítáno</th></tr></thead>
          <tbody>
            {variants.map((v) => (
              <tr key={v.id}>
                <td>{v.label}</td>
                <td className="num">{kc(v[stockKey])}</td>
                <td className="muted num">{v.ordered ? kc(v.ordered) : "—"}</td>
                <td className="num packed-cell">{v[packedKey] ? <strong>{kc(v[packedKey])}</strong> : "—"}</td>
                <td>
                  <input type="number" min="0" style={{ width: 90 }}
                         value={counts[v.id] ?? ""}
                         onChange={(e) => setCounts({ ...counts, [v.id]: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        <strong>Evidence</strong> = kolik má podle systému ležet v regálu — počítej jen to, co tam opravdu je.
        {" "}<strong>Objednáno</strong> = páry v objednávkách, které ještě nejsou zabalené — <em>pořád leží v regálu</em>, počítej je.
        {" "}<strong>📦 Zabaleno</strong> = už nachystané mimo regál (v taškách k předání/odvozu) — <em>tyhle nepočítej</em>, z evidence jsou odečtené.
      </p>
      <button onClick={submit}>Odeslat inventuru ({label}) ke schválení</button>
      {msg && <p className="small">{msg}</p>}
    </div>
  );
}

function CashInventory({ cash }) {
  if (!cash.data) return <div className="card"><h3>Inventura peněz</h3><p className="muted small">Načítám…</p></div>;
  return (
    <>
      {/* téma 8: dvě samostatné sekce — Účet a Pokladna, obě se zadávají a porovnávají */}
      <MoneyCount
        kind="ACCOUNT" title="Inventura účtu"
        expected={cash.data.account_total}
        expectedLabel="Očekáváno na účtu (bankovní převody)"
        inputLabel="Skutečný zůstatek na účtu (Kč)"
        hint="Zůstatek najdeš v bankovnictví. Porovnává se s převodem zaplacenými objednávkami."
        cash={cash}
      />
      <MoneyCount
        kind="CASH" title="Inventura pokladny"
        expected={cash.data.cash_total}
        expectedLabel="Očekávaná hotovost (u holek)"
        inputLabel="Napočítaná hotovost celkem (Kč)"
        hint="Fyzicky přepočítej hotovost."
        cash={cash}
        extra={cash.data.cash_by_person && cash.data.cash_by_person.length > 0 && (
          <div className="subsection">
            <h4>Hotovost podle osoby</h4>
            <table className="list">
              <thead><tr><th>Kdo</th><th className="num">Hotovost</th></tr></thead>
              <tbody>
                {cash.data.cash_by_person.map((p) => (
                  <tr key={p.who}><td>{p.who === "nepřiřazeno" ? "nepřiřazeno (zaplaceno, nepředáno)" : p.who}</td><td className="num">{kc(p.amount)} Kč</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      />
    </>
  );
}

function MoneyCount({ kind, title, expected, expectedLabel, inputLabel, hint, cash, extra }) {
  const [phys, setPhys] = useState("");
  const [msg, setMsg] = useState(null);

  async function save() {
    setMsg(null);
    if (phys === "" || !(Number(phys) >= 0)) { setMsg("Zadej napočítanou částku."); return; }
    try {
      await adminSaveCash({ physical: Number(phys), kind });
      setPhys("");
      cash.reload();
    } catch (e) { setMsg(e.message); }
  }

  return (
    <div className="card">
      <h3>{title}</h3>
      <table className="revenue">
        <tbody>
          <tr><td>{expectedLabel}</td><td className="num"><strong>{kc(expected)} Kč</strong></td></tr>
        </tbody>
      </table>
      {extra}
      <p className="muted small">{hint}</p>
      <label>{inputLabel}
        <input type="number" min="0" value={phys} onChange={(e) => setPhys(e.target.value)} />
      </label>
      {phys !== "" && (
        <p>Rozdíl proti očekávání ({kc(expected)} Kč):{" "}
          <strong>{kc(Number(phys) - expected)} Kč</strong>{" "}
          {Number(phys) === expected ? "✓ sedí" : "⚠️ nesedí"}</p>
      )}
      <button onClick={save}>Odeslat inventuru ke schválení</button>
      {msg && <div className="error">{msg}</div>}
      <p className="muted small">Inventura čeká na schválení superadminem; případný rozdíl jde jako korekce do agendy Korekce. Historie je dole v „Proběhlé inventury".</p>
    </div>
  );
}

function Corrections() {
  const { data, error, reload } = useAsync(adminCorrections, []);
  const [msg, setMsg] = useState(null);

  async function review(id, approve) {
    setMsg(null);
    try {
      if (approve) await adminApproveCorrection(id);
      else await adminRejectCorrection(id);
      reload();
    } catch (e) { setMsg(e.message); }
  }
  async function reviewMoney(id, approve) {
    setMsg(null);
    try {
      await adminMoneyCorrectionAction(id, approve ? "approve" : "reject");
      reload();
    } catch (e) { setMsg(e.message); }
  }

  if (error) return <div className="card error">{error}</div>;
  if (!data) return <div className="card">Načítám…</div>;
  const pending = data.corrections.filter((c) => c.status === "PENDING");
  const moneyAll = data.money_corrections || [];
  const moneyPending = moneyAll.filter((c) => c.status === "PENDING");
  const KIND_LABELS = { CASH: "Pokladna", ACCOUNT: "Účet" };
  return (
    <>
      <div className="card">
        <h3>Korekce ke schválení {pending.length + moneyPending.length ? `(${pending.length + moneyPending.length})` : ""}</h3>
        {msg && <div className="error">{msg}</div>}
        {!pending.length && !moneyPending.length && <p className="muted small">Žádné čekající korekce.</p>}
        <div className="user-cards">
          {pending.map((c) => (
            <div key={c.id} className="user-card">
              <div className="user-card-head">
                <strong>vel. {c.variant_label} · {c.location === "HOLKY" ? "U holek" : "U táty"}</strong>
                <span className={`badge ${c.delta < 0 ? "CANCELLED" : "HANDED_OVER"}`}>{c.delta > 0 ? `+${c.delta}` : c.delta}</span>
              </div>
              <p className="small muted">Evidence {c.expected} → napočítáno {c.physical} · od {c.requested_by || "—"}</p>
              <div className="user-card-actions">
                <button onClick={() => review(c.id, true)}>Schválit</button>{" "}
                <button className="danger" onClick={() => review(c.id, false)}>Zamítnout</button>
              </div>
            </div>
          ))}
          {moneyPending.map((c) => (
            <div key={`m-${c.id}`} className="user-card">
              <div className="user-card-head">
                <strong>💰 {KIND_LABELS[c.kind] || c.kind}</strong>
                <span className={`badge ${c.delta < 0 ? "CANCELLED" : "HANDED_OVER"}`}>{c.delta > 0 ? `+${kc(c.delta)}` : kc(c.delta)} Kč</span>
              </div>
              <p className="small muted">Očekáváno {kc(c.expected)} Kč → napočítáno {kc(c.physical)} Kč · od {c.requested_by || "—"}</p>
              <div className="user-card-actions">
                <button onClick={() => reviewMoney(c.id, true)}>Schválit</button>{" "}
                <button className="danger" onClick={() => reviewMoney(c.id, false)}>Zamítnout</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Přehled všech korekcí</h3>
        <div className="table-scroll">
          <table className="list">
            <thead><tr><th>Kdy</th><th>Co</th><th className="num">Evidence</th><th className="num">Napočítáno</th><th className="num">Δ</th><th>Stav</th><th>Kdo</th></tr></thead>
            <tbody>
              {/* sklady i peníze (účet/pokladna) v jednom přehledu, řazeno dle času */}
              {[
                ...data.corrections.map((c) => ({
                  key: `s-${c.id}`, when: c.created_at,
                  what: `vel. ${c.variant_label} · ${c.location === "HOLKY" ? "U holek" : "U táty"}`,
                  expected: c.expected, physical: c.physical,
                  delta: c.delta > 0 ? `+${c.delta}` : c.delta,
                  status: CORR_STATUS[c.status] || c.status, who: c.requested_by,
                })),
                ...moneyAll.map((c) => ({
                  key: `m-${c.id}`, when: c.created_at,
                  what: `💰 ${KIND_LABELS[c.kind] || c.kind}`,
                  expected: `${kc(c.expected)} Kč`, physical: `${kc(c.physical)} Kč`,
                  delta: `${c.delta > 0 ? `+${kc(c.delta)}` : kc(c.delta)} Kč`,
                  status: CORR_STATUS[c.status] || c.status, who: c.requested_by,
                })),
              ].sort((a, b) => (b.when || "").localeCompare(a.when || "")).map((r) => (
                <tr key={r.key}>
                  <td>{(r.when || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{r.what}</td>
                  <td className="num">{r.expected}</td><td className="num">{r.physical}</td><td className="num">{r.delta}</td>
                  <td>{r.status}</td>
                  <td>{r.who || "—"}</td>
                </tr>
              ))}
              {!data.corrections.length && !moneyAll.length && <tr><td colSpan="7" className="muted">Zatím žádné korekce.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
